import moment from 'moment';
import { sum } from 'utils/calculator';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { types } from 'types/passengers';
import { OUTBOUND, INBOUND, RETURN_JOURNEY, MAX_DAYS_SEARCH, MIN_DAYS_SEARCH } from 'types/cheapestFares';

export const validProductFamiliesForCheapestFaresSearch = (searchParams) => {
  const { productFamilies } = searchParams;

  return !productFamilies?.some((pf) => [productFamilyCodes.EUROSTAR_LOYALTY, productFamilyCodes.DUTY_NON_TOLLABLE].includes(pf));
};

export const buildMainPassenger = ({
  adultsWheelchair,
  childrenWheelchair,
  adults,
  children,
  youths,
  seniors,
  wheelchairCompanions,
  nonWheelchairCompanions,
  guideDogs
}) => {
  if (adults >= 1) {
    return { type: types.ADULT, disabilityType: types.NO_HANDICAP };
  }
  if (seniors >= 1) {
    return { type: types.SENIOR, disabilityType: types.NO_HANDICAP };
  }
  if (youths >= 1) {
    return { type: types.YOUTH, disabilityType: types.NO_HANDICAP };
  }
  if (children >= 1) {
    return { type: types.CHILD, disabilityType: types.NO_HANDICAP };
  }
  if (adultsWheelchair >= 1) {
    return { type: types.ADULT, disabilityType: types.WHEELCHAIR };
  }
  if (childrenWheelchair >= 1) {
    return { type: types.CHILD, disabilityType: types.WHEELCHAIR };
  }
  if (wheelchairCompanions >= 1) {
    return { type: types.ADULT, disabilityType: types.COMPANION };
  }
  if (nonWheelchairCompanions >= 1) {
    return { type: types.ADULT, disabilityType: types.NO_WHEELCHAIR_COMPANION };
  }
  if (guideDogs >= 1) {
    return { type: types.DOG, disabilityType: types.NO_HANDICAP };
  }

  return { type: types.ADULT, disabilityType: types.NO_HANDICAP };
};

export const buildPassengersList = ({
  adultsWheelchair,
  childrenWheelchair,
  adults,
  children,
  youths,
  seniors,
  wheelchairCompanions,
  nonWheelchairCompanions,
  guideDogs
}) => {
  const getAdults = [...Array(adults || 0)].map(() => ({
    type: types.ADULT,
    disabilityType: types.NO_HANDICAP
  }));
  const getSeniors = [...Array(seniors || 0)].map(() => ({
    type: types.SENIOR,
    disabilityType: types.NO_HANDICAP
  }));
  const getYouths = [...Array(youths || 0)].map(() => ({
    type: types.YOUTH,
    disabilityType: types.NO_HANDICAP
  }));
  const getChildren = [...Array(children || 0)].map(() => ({
    type: types.CHILD,
    disabilityType: types.NO_HANDICAP
  }));
  const getAdultsWheelchair = [...Array(adultsWheelchair || 0)].map(() => ({
    type: types.ADULT,
    disabilityType: types.WHEELCHAIR
  }));
  const getChildrenWheelchair = [...Array(childrenWheelchair || 0)].map(() => ({
    type: types.CHILD,
    disabilityType: types.WHEELCHAIR
  }));
  const getWheelchairCompanions = [...Array(wheelchairCompanions || 0)].map(() => ({
    type: types.ADULT,
    disabilityType: types.COMPANION
  }));
  const getNonWheelchairCompanions = [...Array(nonWheelchairCompanions || 0)].map(() => ({
    type: types.ADULT,
    disabilityType: types.NO_WHEELCHAIR_COMPANION
  }));
  const getGuideDogs = [...Array(guideDogs || 0)].map(() => ({
    type: types.DOG,
    disabilityType: types.NO_HANDICAP
  }));

  return getAdults
    .concat(getSeniors)
    .concat(getYouths)
    .concat(getChildren)
    .concat(getAdultsWheelchair)
    .concat(getChildrenWheelchair)
    .concat(getWheelchairCompanions)
    .concat(getNonWheelchairCompanions)
    .concat(getGuideDogs);
};

export const buildNumberOfPassengers = ({
  adults,
  adults12To15,
  adults16Plus,
  adultsWheelchair,
  children,
  children4Only,
  children5To11,
  childrenWheelchair,
  youths,
  seniors,
  guideDogs,
  infants,
  nonWheelchairCompanions,
  wheelchairCompanions
}) =>
  sum([
    adults,
    adults12To15,
    adults16Plus,
    adultsWheelchair,
    children,
    children4Only,
    children5To11,
    childrenWheelchair,
    youths,
    seniors,
    guideDogs,
    infants,
    nonWheelchairCompanions,
    wheelchairCompanions
  ]);

export const buildCheapestFaresSearchRequest = (searchParams) => {
  const passengersList = buildPassengersList(searchParams);
  const mainPassengerType = buildMainPassenger(searchParams);
  const numberOfPassenger = buildNumberOfPassengers(searchParams);
  const { currency, productFamilies, contractCode, origin, outboundDate, destination, inboundDate } = searchParams;
  const isReturnJourney = !!inboundDate;

  const outboundCheapestFareList = {
    destination,
    direction: OUTBOUND,
    endDate: moment(outboundDate).add(MAX_DAYS_SEARCH, 'days').format('YYYY-MM-DD'),
    origin,
    startDate: moment(outboundDate).subtract(MIN_DAYS_SEARCH, 'days').format('YYYY-MM-DD')
  };

  const inboundCheapestFareList = {
    destination: origin,
    direction: INBOUND,
    endDate: moment(inboundDate).add(MAX_DAYS_SEARCH, 'days').format('YYYY-MM-DD'),
    origin: destination,
    startDate: moment(inboundDate).subtract(MIN_DAYS_SEARCH, 'days').format('YYYY-MM-DD')
  };

  return isReturnJourney
    ? {
        currency,
        mainPassengerType,
        passengersList,
        numberOfPassenger,
        productFamiliesSearch: productFamilies,
        contractCodeSearch: contractCode,
        cheapestFaresLists: [
          { ...outboundCheapestFareList, journeyType: RETURN_JOURNEY },
          { ...inboundCheapestFareList, journeyType: RETURN_JOURNEY }
        ],
        isTotalPriceSearch: true
      }
    : {
        currency,
        mainPassengerType,
        passengersList,
        numberOfPassenger,
        productFamiliesSearch: productFamilies,
        contractCodeSearch: contractCode,
        cheapestFaresLists: [outboundCheapestFareList],
        isTotalPriceSearch: true
      };
};
